/**
 * AiqQuizStage - AIQ 테스트 전용 퀴즈 렌더러
 *
 * QuizStage를 기반으로 AIQ API를 사용하도록 수정한 버전입니다.
 * LegacyXXXQuiz 컴포넌트를 사용하여 퀴즈를 렌더링합니다.
 */

import { useState, useCallback, useMemo, useRef } from 'react'
import {
    ChevronLeft,
    ChevronRight,
    Loader2,
    ArrowLeft,
    CheckCircle,
    XCircle,
    Trophy,
    RefreshCw,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@quebon/ui/components/dialog'
import { quizScriptApi } from '@/api/quizScript'
import { AIQ_QUIZ_OPTIONS } from '@/types/quiz'
import { toast } from 'sonner'
import type {
    QuizScript,
    QuizSolveResult,
    MultiChoiceQuizData,
    QuizData,
} from '@quebon/shared-learning'
import {
    QuizRenderer,
} from '@quebon/shared-learning'

// ============================================================================
// Types
// ============================================================================

export interface AiqQuizItem {
    quizId: string
    sequenceOrder: number
}

export interface AiqQuizDataResponse {
    quizzes: AiqQuizItem[]
    totalQuestions: number
}

export interface AiqPreviousResponse {
    quizId: string
    isCorrect: boolean
    submittedResponse: (string | number | boolean)[]
    corrects: boolean[]
    correctAnswers: (string | number | boolean)[]
    vars?: Record<string, unknown>  // 제출 시 사용된 변수 값
}

export interface AiqQuizStageProps {
    productId: number
    testType: 'pre' | 'post'
    sessionId: number
    quizzes: QuizScript[]
    resumeFromIndex?: number  // 이어풀기 시 시작할 문제 인덱스
    previousResponses?: AiqPreviousResponse[]  // 이전 응답 데이터 (재개 시)
    onComplete: (result: AiqCompletionResult) => void
    onBack: () => void
    submitResponse: (quizId: string, request: AiqSubmitRequest) => Promise<AiqSubmitResponse>
    completeSession: () => Promise<void>
}

export interface AiqSubmitRequest {
    answers: (string | number | boolean)[]
    vars?: Record<string, unknown>
    elapsedSec?: number
}

export interface AiqSubmitResponse {
    correct: boolean  // API returns 'correct', not 'isCorrect'
    corrects?: (boolean | string)[]
    correctAnswers?: (string | number | boolean)[]
    description?: {
        text?: string
        imageUrl?: string
    }
    difficultyLevel?: number  // 난이도 레벨 (1-5, 디버깅용)
}

export interface AiqCompletionResult {
    score: number
    correctCount: number
    totalQuestions: number
    passed: boolean
}

type QuizStatus = 'unsolved' | 'current' | 'correct' | 'incorrect' | 'submitted' | 'submitted-empty'

interface QuizResultInfo {
    isCorrect: boolean
    solveResult: QuizSolveResult
}

// ============================================================================
// Helper Functions
// ============================================================================

function getAiqQuizTitle(type: 'pre' | 'post', t: (key: string) => string): string {
    return type === 'pre' ? t('learning.aiqQuiz.titlePre') : t('learning.aiqQuiz.titlePost')
}

function getAiqQuizDescription(type: 'pre' | 'post', t: (key: string) => string): string {
    return type === 'pre'
        ? t('learning.aiqQuiz.descPre')
        : t('learning.aiqQuiz.descPost')
}

// ============================================================================
// Component
// ============================================================================

export function AiqQuizStage({
    productId,
    testType,
    sessionId,
    quizzes,
    resumeFromIndex = 0,
    previousResponses = [],
    onComplete,
    onBack,
    submitResponse,
    completeSession,
}: AiqQuizStageProps) {
    const { t } = useTranslation('learner')

    // Behavior options (AIQ uses fixed options)
    const {
        sequentialNavigation,
        showImmediateFeedback,
        allowRetry,
    } = AIQ_QUIZ_OPTIONS

    // State
    const [quizScripts, setQuizScripts] = useState<Map<string, QuizScript>>(() => {
        // Initialize with provided quizzes
        const map = new Map<string, QuizScript>()
        quizzes.forEach((q) => map.set(String(q.quizId), q))
        return map
    })
    // 재개 시 이미 답변한 문제 다음부터 시작 (답변한 문제 수가 전체보다 작을 때만)
    const [currentIndex, setCurrentIndex] = useState(
        resumeFromIndex > 0 && resumeFromIndex < quizzes.length ? resumeFromIndex : 0
    )
    // 이전 응답으로 resultsByQuizId 초기화
    const [resultsByQuizId, setResultsByQuizId] = useState<Map<string, QuizResultInfo>>(() => {
        const initialMap = new Map<string, QuizResultInfo>()
        if (previousResponses && previousResponses.length > 0) {
            previousResponses.forEach(prev => {
                const solveResult: QuizSolveResult = {
                    correct: prev.isCorrect,
                    corrects: prev.corrects ?? [],
                    correctAnswers: (prev.correctAnswers ?? []).map(a => String(a)),
                    elapsedTime: 0,
                }
                initialMap.set(prev.quizId, {
                    isCorrect: prev.isCorrect,
                    solveResult,
                })
            })
        }
        return initialMap
    })
    // 이전 응답으로 submittedAnswersByQuizId 초기화
    const [submittedAnswersByQuizId, setSubmittedAnswersByQuizId] = useState<Map<string, string[]>>(() => {
        const initialMap = new Map<string, string[]>()
        if (previousResponses && previousResponses.length > 0) {
            previousResponses.forEach(prev => {
                if (prev.submittedResponse && Array.isArray(prev.submittedResponse)) {
                    initialMap.set(prev.quizId, prev.submittedResponse.map(a => String(a)))
                }
            })
        }
        return initialMap
    })
    // 이전 응답의 vars 저장 (allowRetry 시 동일한 문제 표시를 위해)
    const [varsByQuizId] = useState<Map<string, Record<string, unknown>>>(() => {
        const initialMap = new Map<string, Record<string, unknown>>()
        if (previousResponses && previousResponses.length > 0) {
            previousResponses.forEach(prev => {
                if (prev.vars) {
                    initialMap.set(prev.quizId, prev.vars)
                }
            })
        }
        return initialMap
    })
    const [isSolving, setIsSolving] = useState(false)
    const [isLoadingQuiz, setIsLoadingQuiz] = useState(false)
    const [isCompleting, setIsCompleting] = useState(false)
    const [isCompleted, setIsCompleted] = useState(false)
    const [showSubmitConfirm, setShowSubmitConfirm] = useState(false)

    // 현재 입력 중인 답변 (아직 제출 안됨) - onAnswerChange 콜백으로 업데이트
    const [pendingAnswersByQuizId, setPendingAnswersByQuizId] = useState<Map<string, string[]>>(new Map())

    // 문제별 시작 시간 추적 (elapsedSec 계산용)
    const quizStartTimeRef = useRef<number>(Date.now())

    // Computed values
    const quizItems = quizzes
    const currentQuizItem = quizItems[currentIndex]
    const currentQuizId = currentQuizItem ? String(currentQuizItem.quizId) : ''
    const currentResult = resultsByQuizId.get(currentQuizId)
    const currentQuizScript = quizScripts.get(currentQuizId)

    // submitted answers를 quiz type에 맞게 변환 (인덱스 → choice key)
    const currentSubmittedAnswers = useMemo(() => {
        const rawAnswers = submittedAnswersByQuizId.get(currentQuizId)
        if (!rawAnswers || rawAnswers.length === 0 || !currentQuizScript) return rawAnswers

        // multi_choice 타입인지 확인
        if (currentQuizScript.type !== 'multi_choice') return rawAnswers

        // choices 추출
        const choices = currentQuizScript.choices
        if (!choices || choices.length === 0) return rawAnswers

        // vars에서 random 배열 추출 (shuffle 정보)
        const vars = currentQuizScript.vars as Record<string, unknown> | null | undefined
        const random = vars?.random as number[] | undefined

        // 인덱스인지 확인 (모든 값이 숫자 문자열이고, choice key에 없는 경우)
        const allChoiceKeys = choices.map(c => c.key)
        const isIndexBased = rawAnswers.every(a => {
            const num = parseInt(a, 10)
            return !isNaN(num) && !allChoiceKeys.includes(a)
        })

        if (isIndexBased) {
            // submitted_response는 원본 choices 인덱스 (1-indexed)
            // 원본 인덱스로 직접 choices에서 key 가져오기
            return rawAnswers
                .map(a => {
                    const originalIdx = parseInt(a, 10) - 1  // 1-indexed → 0-indexed
                    return originalIdx >= 0 && originalIdx < choices.length
                        ? choices[originalIdx].key
                        : null
                })
                .filter((k): k is string => k !== null)
        }

        return rawAnswers
    }, [submittedAnswersByQuizId, currentQuizId, currentQuizScript])

    // 배열 비교 헬퍼 함수
    const arraysEqual = useCallback((a?: string[], b?: string[]): boolean => {
        if (!a && !b) return true
        if (!a || !b) return false
        if (a.length !== b.length) return false
        return a.every((val, idx) => val === b[idx])
    }, [])

    // 퀴즈 컴포넌트에서 답변 변경 시 호출되는 콜백
    const handleAnswerChange = useCallback((answers: string[]) => {
        setPendingAnswersByQuizId(prev => new Map(prev).set(currentQuizId, answers))
    }, [currentQuizId])

    // Load quiz script for current quiz if not already loaded
    const loadQuizScript = useCallback(async (quizId: string) => {
        if (quizScripts.has(quizId)) return quizScripts.get(quizId)!

        setIsLoadingQuiz(true)
        try {
            // allowRetry 시 이전에 제출할 때 사용된 vars로 문제 로드 (일관성 유지)
            const storedVars = varsByQuizId.get(quizId)
            const script = await quizScriptApi.getQuiz(quizId, false, storedVars)
            setQuizScripts(prev => new Map(prev).set(quizId, script))
            return script
        } catch (error) {
            console.error('Failed to load quiz script:', error)
            return null
        } finally {
            setIsLoadingQuiz(false)
        }
    }, [quizScripts, varsByQuizId])

    // Get quiz status for navigation
    const getQuizStatus = useCallback((index: number): QuizStatus => {
        if (index === currentIndex) return 'current'

        const quiz = quizItems[index]
        const quizId = quiz ? String(quiz.quizId) : ''
        const result = resultsByQuizId.get(quizId)

        if (!result) return 'unsolved'

        // 제출했지만 빈 답변인지 확인
        if (!showImmediateFeedback) {
            const submitted = submittedAnswersByQuizId.get(quizId)
            if (!submitted || submitted.length === 0 || submitted.every(a => a === '')) {
                return 'submitted-empty'
            }
            return 'submitted'
        }

        return result.isCorrect ? 'correct' : 'incorrect'
    }, [currentIndex, quizItems, resultsByQuizId, showImmediateFeedback, submittedAnswersByQuizId])

    // 퀴즈 컴포넌트에 전달할 solveResult 결정
    const getSolveResultForDisplay = useCallback((quizId: string): QuizSolveResult | undefined => {
        const result = resultsByQuizId.get(quizId)
        if (!result) return undefined

        if (allowRetry) {
            return undefined
        }

        if (!showImmediateFeedback) {
            return {
                ...result.solveResult,
                corrects: [],
                correctAnswers: [],
                description: undefined,
            }
        }

        return result.solveResult
    }, [resultsByQuizId, allowRetry, showImmediateFeedback])

    // 특정 인덱스로 이동 가능한지 확인
    const canNavigateTo = useCallback((index: number): boolean => {
        if (!sequentialNavigation) return true
        if (index <= currentIndex) return true
        for (let i = 0; i < index; i++) {
            const quizId = quizItems[i] ? String(quizItems[i].quizId) : ''
            if (quizId && !resultsByQuizId.has(quizId)) return false
        }
        return true
    }, [sequentialNavigation, currentIndex, quizItems, resultsByQuizId])

    // Convert QuizScript to legacy QuizData format
    const getLegacyQuizData = useCallback((quizScript: QuizScript): QuizData | null => {
        if (!quizScript) return null

        const rawData = quizScript as unknown as Record<string, unknown>
        const qd = (rawData.quizData as QuizData) || quizScript

        return {
            ...qd,
            type: quizScript.type || qd.type,
            title: quizScript.title || qd.title,
            problem: quizScript.problem || qd.problem,
            vars: quizScript.vars || qd.vars,
            image: quizScript.image || qd.image,
            imageMobile: quizScript.imageMobile || qd.imageMobile,
        } as QuizData
    }, [])

    // Handle quiz solve
    const handleSolve = useCallback(
        async (
            _quizId: string | number,
            answers: string[],
            quizVars: Record<string, unknown>,
            elapsedTime: number
        ) => {
            if (!currentQuizScript || isSolving) return

            const quizId = currentQuizId

            setSubmittedAnswersByQuizId(prev => new Map(prev).set(quizId, answers))
            setIsSolving(true)

            const legacyQuiz = getLegacyQuizData(currentQuizScript)
            if (!legacyQuiz) {
                setIsSolving(false)
                return
            }

            try {
                // Transform answers based on quiz type for the solve API
                let transformedAnswers: (string | number | boolean)[] = []

                switch (legacyQuiz.type) {
                    case 'fill_blank':
                        transformedAnswers = answers
                        break

                    case 'multi_choice': {
                        const quiz = legacyQuiz as MultiChoiceQuizData
                        transformedAnswers = answers
                            .map((key) => {
                                const index = quiz.choices.findIndex((choice) => choice.key === key)
                                return index >= 0 ? index + 1 : 0
                            })
                            .filter((idx) => idx > 0)
                        break
                    }

                    case 'select_text': {
                        transformedAnswers = answers.map((a) => {
                            const num = parseInt(a, 10)
                            return isNaN(num) ? 0 : num
                        })
                        break
                    }

                    case 'boolean': {
                        transformedAnswers = answers.map((a) => a === 'true')
                        break
                    }

                    case 'drag_text':
                        transformedAnswers = answers
                        break

                    default:
                        transformedAnswers = []
                }

                // Submit to AIQ API
                const responseData = await submitResponse(quizId, {
                    answers: transformedAnswers,
                    vars: quizVars ?? {},
                    elapsedSec: Math.floor(elapsedTime / 1000),
                })

                // Convert response to QuizSolveResult
                const solveResult: QuizSolveResult = {
                    correct: responseData.correct,
                    corrects: (responseData.corrects ?? []).map((c: boolean | string) => {
                        if (typeof c === 'boolean') return c
                        if (c === 'true') return true
                        if (c === 'false') return false
                        return Boolean(c)
                    }),
                    correctAnswers: (responseData.correctAnswers ?? []).map((a) => {
                        if (typeof a === 'string') return a
                        if (typeof a === 'number') return String(a)
                        if (typeof a === 'boolean') return String(a)
                        return String(a)
                    }),
                    elapsedTime,
                    description: responseData.description
                        ? {
                            text: responseData.description.text ?? undefined,
                            image: responseData.description.imageUrl
                                ? { signedUrl: responseData.description.imageUrl }
                                : undefined,
                            imageMobile: undefined,
                            desmos: undefined,
                        }
                        : undefined,
                }

                setResultsByQuizId(prev => new Map(prev).set(quizId, {
                    isCorrect: responseData.correct,
                    solveResult,
                }))

                // allowRetry: 제출 후 처리
                if (allowRetry) {
                    // 자유 이동: 자동 진행/완료 없이 현재 문제에 머무름
                    if (!sequentialNavigation) return

                    const isLastQuestion = currentIndex >= quizItems.length - 1
                    if (isLastQuestion) {
                        // 마지막 문제: 완료 처리
                        try {
                            await completeSession()
                            // 인라인 점수 계산 (useMemo 대신)
                            const answered = quizItems.filter(item => resultsByQuizId.has(String(item.quizId)))
                            const correctItems = answered.filter(item => resultsByQuizId.get(String(item.quizId))?.isCorrect)
                            const total = quizItems.length
                            const correct = correctItems.length
                            const percentage = answered.length > 0 ? Math.round((correct / answered.length) * 100) : 0
                            setIsCompleted(true)
                            onComplete({
                                score: percentage,
                                correctCount: correct,
                                totalQuestions: total,
                                passed: percentage >= 60,
                            })
                        } catch (completeErr) {
                            console.error('Failed to complete session:', completeErr)
                            toast.error(t('learning.aiqQuiz.sessionCompleteError'))
                        }
                    } else {
                        // 다음 문제로 이동
                        const nextIndex = currentIndex + 1
                        setCurrentIndex(nextIndex)
                        quizStartTimeRef.current = Date.now()
                        const nextQuizId = quizItems[nextIndex] ? String(quizItems[nextIndex].quizId) : ''
                        if (nextQuizId) {
                            loadQuizScript(nextQuizId)
                        }
                    }
                }
            } catch (err) {
                console.error('Failed to solve quiz:', err)

                // Create error result
                const errorResult: QuizSolveResult = {
                    correct: false,
                    corrects: [],
                    correctAnswers: [],
                    elapsedTime,
                }
                setResultsByQuizId(prev => new Map(prev).set(quizId, {
                    isCorrect: false,
                    solveResult: errorResult,
                }))
            } finally {
                setIsSolving(false)
            }
        },
        [currentQuizScript, currentQuizId, isSolving, getLegacyQuizData, submitResponse, allowRetry, currentIndex, quizItems, loadQuizScript, completeSession, resultsByQuizId, onComplete]
    )

    // Navigation handlers
    // 현재 문제의 pending 답변을 제출하고 결과 반환
    const submitCurrentPendingAnswer = useCallback(async (): Promise<boolean> => {
        const quizId = currentQuizId
        const pending = pendingAnswersByQuizId.get(quizId)
        const submitted = submittedAnswersByQuizId.get(quizId)
        const hasSubmitted = submittedAnswersByQuizId.has(quizId)

        // 이미 제출한 적이 있고 답변 변경 없으면 스킵
        if (hasSubmitted) {
            if (!pending) return true // 상호작용 없이 재방문 → 변경 없음
            if (arraysEqual(pending, submitted)) return true
        }

        // 퀴즈 스크립트가 없으면 스킵
        if (!currentQuizScript) {
            return true
        }

        const legacyQuiz = getLegacyQuizData(currentQuizScript)
        if (!legacyQuiz) return true

        // pending이 없으면 빈 답변으로 제출 (문제 확인만 하고 답 입력 안한 경우)
        const answersToSubmit = pending || []

        try {
            // Transform answers based on quiz type
            let transformedAnswers: (string | number | boolean)[] = []

            switch (legacyQuiz.type) {
                case 'fill_blank':
                    transformedAnswers = answersToSubmit
                    break
                case 'multi_choice': {
                    const quiz = legacyQuiz as MultiChoiceQuizData
                    transformedAnswers = answersToSubmit
                        .map((key) => {
                            const index = quiz.choices.findIndex((choice) => choice.key === key)
                            return index >= 0 ? index + 1 : 0
                        })
                        .filter((idx) => idx > 0)
                    break
                }
                case 'select_text':
                    transformedAnswers = answersToSubmit.map((a) => {
                        const num = parseInt(a, 10)
                        return isNaN(num) ? 0 : num
                    })
                    break
                case 'boolean':
                    transformedAnswers = answersToSubmit.map((a) => a === 'true')
                    break
                case 'drag_text':
                    transformedAnswers = answersToSubmit
                    break
                default:
                    transformedAnswers = []
            }

            const elapsedMs = Date.now() - quizStartTimeRef.current
            const elapsedSec = Math.floor(elapsedMs / 1000)

            const responseData = await submitResponse(quizId, {
                answers: transformedAnswers,
                vars: currentQuizScript.vars ?? {},
                elapsedSec,
            })

            // 타이머 리셋
            quizStartTimeRef.current = Date.now()

            // Update results
            const solveResult: QuizSolveResult = {
                correct: responseData.correct,
                corrects: (responseData.corrects ?? []).map((c) => Boolean(c)),
                correctAnswers: (responseData.correctAnswers ?? []).map((a) => String(a)),
                elapsedTime: elapsedMs,
            }

            setResultsByQuizId(prev => new Map(prev).set(quizId, {
                isCorrect: responseData.correct,
                solveResult,
            }))
            setSubmittedAnswersByQuizId(prev => new Map(prev).set(quizId, answersToSubmit))

            return true
        } catch (err) {
            console.error('Failed to auto-save quiz answer:', err)
            return false
        }
    }, [currentQuizId, pendingAnswersByQuizId, submittedAnswersByQuizId, arraysEqual, currentQuizScript, getLegacyQuizData, submitResponse])

    const handlePrevious = useCallback(async () => {
        if (currentIndex > 0) {
            // allowRetry인 경우 이동 전 자동 저장
            if (allowRetry) {
                await submitCurrentPendingAnswer()
            }
            const newIndex = currentIndex - 1
            setCurrentIndex(newIndex)
            quizStartTimeRef.current = Date.now()
            const newQuizId = quizItems[newIndex] ? String(quizItems[newIndex].quizId) : ''
            if (newQuizId) {
                loadQuizScript(newQuizId)
            }
        }
    }, [currentIndex, quizItems, loadQuizScript, allowRetry, submitCurrentPendingAnswer])

    const handleNext = useCallback(async () => {
        if (currentIndex < quizItems.length - 1) {
            // allowRetry인 경우 이동 전 자동 저장
            if (allowRetry) {
                await submitCurrentPendingAnswer()
            }
            const newIndex = currentIndex + 1
            setCurrentIndex(newIndex)
            quizStartTimeRef.current = Date.now()
            const newQuizId = quizItems[newIndex] ? String(quizItems[newIndex].quizId) : ''
            if (newQuizId) {
                loadQuizScript(newQuizId)
            }
        }
    }, [currentIndex, quizItems, loadQuizScript, allowRetry, submitCurrentPendingAnswer])

    const handleNavClick = useCallback(async (index: number) => {
        if (index === currentIndex) return
        if (!canNavigateTo(index)) return
        // allowRetry인 경우 이동 전 자동 저장
        if (allowRetry) {
            await submitCurrentPendingAnswer()
        }
        setCurrentIndex(index)
        quizStartTimeRef.current = Date.now()
        const newQuizId = quizItems[index] ? String(quizItems[index].quizId) : ''
        if (newQuizId) {
            loadQuizScript(newQuizId)
        }
    }, [currentIndex, quizItems, loadQuizScript, canNavigateTo, allowRetry, submitCurrentPendingAnswer])

    // Check if can proceed to next
    const canGoNext = useMemo(() => {
        if (currentIndex >= quizItems.length - 1) return false

        // 자유 이동: 항상 다음 문제로 이동 가능
        if (!sequentialNavigation) return true

        // allowRetry: pending 답변이 있으면 다음으로 이동 가능
        if (allowRetry) {
            const pending = pendingAnswersByQuizId.get(currentQuizId)
            if (pending && pending.length > 0 && pending.some(a => a !== '')) {
                return true
            }
            return resultsByQuizId.has(currentQuizId)
        }

        return resultsByQuizId.has(currentQuizId)
    }, [currentIndex, quizItems.length, resultsByQuizId, currentQuizId, allowRetry, pendingAnswersByQuizId, sequentialNavigation])

    // Check if all questions answered
    const allQuestionsAnswered = useMemo(() => {
        return quizItems.every(item => {
            const quizId = String(item.quizId)
            // 이미 제출된 답변이 있으면 OK
            if (resultsByQuizId.has(quizId)) return true
            // allowRetry: pending 답변이 있으면 OK
            if (allowRetry) {
                const pending = pendingAnswersByQuizId.get(quizId)
                return pending && pending.length > 0 && pending.some(a => a !== '')
            }
            return false
        })
    }, [quizItems, resultsByQuizId, allowRetry, pendingAnswersByQuizId])

    // Calculate score
    const calculateScore = useMemo(() => {
        const answered = quizItems.filter(item => resultsByQuizId.has(String(item.quizId)))
        const correct = answered.filter(item => resultsByQuizId.get(String(item.quizId))?.isCorrect)
        return {
            total: quizItems.length,
            answered: answered.length,
            correct: correct.length,
            percentage: answered.length > 0 ? Math.round((correct.length / answered.length) * 100) : 0,
        }
    }, [quizItems, resultsByQuizId])

    // 미응답 문제 수 (미제출 + 빈 답변 제출 포함)
    const unansweredCount = useMemo(() => {
        return quizItems.filter(item => {
            const quizId = String(item.quizId)

            // 현재 문제: pending 답변 또는 이전 제출 확인
            if (quizId === currentQuizId) {
                const pending = pendingAnswersByQuizId.get(quizId)
                if (pending && pending.length > 0 && pending.some(a => a !== '')) return false
                const submitted = submittedAnswersByQuizId.get(quizId)
                if (submitted && submitted.length > 0 && submitted.some(a => a !== '')) return false
                return true
            }

            // 다른 문제: 제출 상태 확인
            if (!resultsByQuizId.has(quizId)) return true // 미제출
            const submitted = submittedAnswersByQuizId.get(quizId)
            if (!submitted || submitted.length === 0 || submitted.every(a => a === '')) return true // 빈 답변
            return false
        }).length
    }, [quizItems, resultsByQuizId, submittedAnswersByQuizId, currentQuizId, pendingAnswersByQuizId])

    // Handle session completion
    const handleCompleteSession = useCallback(async () => {
        setIsCompleting(true)
        try {
            // 현재 문제의 pending 답변 제출
            if (allowRetry) {
                await submitCurrentPendingAnswer()
            }

            await completeSession()

            const { total, correct, percentage } = calculateScore
            setIsCompleted(true)

            onComplete({
                score: percentage,
                correctCount: correct,
                totalQuestions: total,
                passed: percentage >= 60,
            })
        } catch (error) {
            console.error('Failed to complete session:', error)
            toast.error(t('learning.aiqQuiz.sessionCompleteError'))
        } finally {
            setIsCompleting(false)
        }
    }, [completeSession, calculateScore, onComplete, allowRetry, submitCurrentPendingAnswer])

    // 완료 버튼 클릭: 미응답 문제가 있으면 확인 다이얼로그 표시
    const handleCompleteClick = useCallback(() => {
        if (unansweredCount > 0) {
            setShowSubmitConfirm(true)
            return
        }
        handleCompleteSession()
    }, [unansweredCount, handleCompleteSession])

    // 확인 다이얼로그에서 제출 확정
    const handleConfirmSubmit = useCallback(() => {
        setShowSubmitConfirm(false)
        handleCompleteSession()
    }, [handleCompleteSession])

    // Get legacy quiz data for rendering
    const legacyQuiz = currentQuizScript ? getLegacyQuizData(currentQuizScript) : null

    // ========================================================================
    // Render: Loading state
    // ========================================================================
    if (isLoadingQuiz && !currentQuizScript) {
        return (
            <div className="flex flex-col justify-center items-center py-20 bg-white rounded-2xl border border-gray-100 shadow-sm">
                <Loader2 className="w-10 h-10 text-indigo-600 animate-spin" />
                <p className="mt-4 text-gray-600">{t('learning.aiqQuiz.loadingQuiz')}</p>
            </div>
        )
    }

    // ========================================================================
    // Render: Completed state
    // ========================================================================
    if (isCompleted) {
        const { total, correct, percentage } = calculateScore

        return (
            <div className="overflow-hidden bg-white rounded-2xl border border-gray-100 shadow-sm">
                <div className="p-8 text-center">
                    {/* Trophy Icon */}
                    <div className={cn(
                        "flex justify-center items-center mx-auto mb-6 w-24 h-24 rounded-full",
                        percentage >= 80 ? "bg-green-100" :
                            percentage >= 50 ? "bg-yellow-100" : "bg-red-100"
                    )}>
                        <Trophy className={cn(
                            "w-12 h-12",
                            percentage >= 80 ? "text-green-600" :
                                percentage >= 50 ? "text-yellow-600" : "text-red-600"
                        )} />
                    </div>

                    {/* Score */}
                    <h2 className="mb-2 text-2xl font-bold text-gray-900">
                        {t('learning.aiqQuiz.completedTitle', { quizTitle: getAiqQuizTitle(testType, t) })}
                    </h2>
                    <p className="mb-6 text-lg text-gray-600">
                        {t('learning.aiqQuiz.completedScore', { total, correct })}
                    </p>

                    {/* Progress Bar */}
                    <div className="mx-auto mb-6 max-w-sm">
                        <div className="flex justify-between mb-2 text-sm text-gray-600">
                            <span>{t('learning.aiqQuiz.accuracyRate')}</span>
                            <span className="font-semibold">{percentage}%</span>
                        </div>
                        <div className="w-full h-3 bg-gray-200 rounded-full">
                            <div
                                className={cn(
                                    "h-3 rounded-full transition-all duration-500",
                                    percentage >= 80 ? "bg-green-500" :
                                        percentage >= 50 ? "bg-yellow-500" : "bg-red-500"
                                )}
                                style={{ width: `${percentage}%` }}
                            />
                        </div>
                    </div>

                    {/* Message */}
                    <div className={cn(
                        "p-4 rounded-lg mb-8 max-w-sm mx-auto",
                        percentage >= 80 ? "bg-green-50 text-green-800" :
                            percentage >= 50 ? "bg-yellow-50 text-yellow-800" : "bg-red-50 text-red-800"
                    )}>
                        {percentage >= 80 ? (
                            <p>{t('learning.aiqQuiz.msgHigh')}</p>
                        ) : percentage >= 50 ? (
                            <p>{t('learning.aiqQuiz.msgMid')}</p>
                        ) : (
                            <p>{t('learning.aiqQuiz.msgLow')}</p>
                        )}
                    </div>

                    {/* Actions */}
                    <button
                        onClick={onBack}
                        className="px-8 py-3 font-semibold text-white bg-indigo-600 rounded-lg transition-colors hover:bg-indigo-700"
                    >
                        {t('learning.aiqQuiz.goBack')}
                    </button>
                </div>
            </div>
        )
    }

    // ========================================================================
    // Render: No quizzes
    // ========================================================================
    if (quizItems.length === 0) {
        return (
            <div className="overflow-hidden bg-white rounded-2xl border border-gray-100 shadow-sm">
                <div className="flex flex-col justify-center items-center p-8 text-center">
                    <p className="mb-4 text-gray-500">{t('learning.aiqQuiz.noQuizzes')}</p>
                    <button
                        onClick={onBack}
                        className="flex gap-2 items-center text-indigo-600 hover:text-indigo-800"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        {t('learning.aiqQuiz.goBack')}
                    </button>
                </div>
            </div>
        )
    }

    // ========================================================================
    // Render: Main Quiz UI
    // ========================================================================
    return (
        <div className="overflow-hidden bg-white rounded-2xl border border-gray-100 shadow-sm">
            {/* Header */}
            <div className={cn(
                "px-6 py-4 bg-gradient-to-r",
                testType === 'pre' ? "from-blue-600 to-indigo-600" : "from-purple-600 to-indigo-600"
            )}>
                <div className="flex justify-between items-center">
                    <div>
                        <button
                            onClick={onBack}
                            className="flex gap-2 items-center mb-2 text-sm text-indigo-200 transition-colors hover:text-white"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            {t('learning.aiqQuiz.goBack')}
                        </button>
                        <h2 className="text-xl font-bold text-white">{getAiqQuizTitle(testType, t)}</h2>
                        <p className="text-sm text-indigo-200">{getAiqQuizDescription(testType, t)}</p>
                    </div>
                    <div className="text-right">
                        <p className="text-2xl font-bold text-white">
                            {currentIndex + 1} / {quizItems.length}
                        </p>
                        {showImmediateFeedback && (
                            <p className="text-sm text-indigo-200">
                                {t('learning.aiqQuiz.correctCount', { correct: calculateScore.correct, total: calculateScore.total })}
                            </p>
                        )}
                    </div>
                </div>
            </div>

            {/* Quiz Navigation */}
            <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
                <div className="flex overflow-x-auto gap-2 p-1 custom-scrollbar">
                    {quizItems.map((quiz, index) => {
                        const status = getQuizStatus(index)
                        const quizId = String(quiz.quizId)
                        const isNavigable = canNavigateTo(index)

                        return (
                            <button
                                key={quizId}
                                onClick={() => handleNavClick(index)}
                                disabled={!isNavigable}
                                className={cn(
                                    'flex-shrink-0 w-10 h-10 rounded-lg font-medium text-sm transition-all flex items-center justify-center',
                                    status === 'unsolved' && isNavigable && 'border-2 border-gray-300 bg-white text-gray-600 hover:border-gray-400',
                                    status === 'unsolved' && !isNavigable && 'border-2 border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed opacity-50',
                                    status === 'submitted-empty' && 'border-2 border-dashed border-amber-400 bg-white text-amber-600',
                                    status === 'current' && 'ring-2 ring-indigo-500 ring-offset-2 bg-indigo-600 text-white',
                                    status === 'correct' && 'bg-emerald-500 text-white',
                                    status === 'incorrect' && 'bg-rose-500 text-white',
                                    status === 'submitted' && 'bg-gray-500 text-white'
                                )}
                            >
                                {status === 'correct' && <CheckCircle className="w-5 h-5" />}
                                {status === 'incorrect' && <XCircle className="w-5 h-5" />}
                                {status === 'submitted' && <CheckCircle className="w-5 h-5" />}
                                {(status === 'unsolved' || status === 'current' || status === 'submitted-empty') && (index + 1)}
                            </button>
                        )
                    })}
                </div>
            </div>

            {/* Quiz Content */}
            <div className="p-6">
                {isLoadingQuiz ? (
                    <div className="flex flex-col justify-center items-center py-12">
                        <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
                        <p className="mt-3 text-gray-500">{t('learning.aiqQuiz.loadingQuestion')}</p>
                    </div>
                ) : legacyQuiz ? (
                    <div className="mx-auto max-w-3xl">
                        <QuizRenderer
                            key={`${currentQuizId}-${allowRetry ? 'retry' : (currentResult ? 'solved' : 'unsolved')}`}
                            quizType={legacyQuiz.type}
                            quizId={currentQuizId}
                            quiz={legacyQuiz}
                            onSolve={handleSolve}
                            solveResult={getSolveResultForDisplay(currentQuizId)}
                            showReallyTrue={false}
                            useKeypad={false}
                            initialAnswers={currentSubmittedAnswers}
                            hideSubmitButton={allowRetry}
                            onAnswerChange={handleAnswerChange}
                        />

                        {isSolving && (
                            <div className="flex gap-2 justify-center items-center mt-4 text-gray-500">
                                <Loader2 className="w-4 h-4 animate-spin" />
                                <span className="text-sm">{t('learning.aiqQuiz.grading')}</span>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="flex flex-col justify-center items-center py-12">
                        <p className="text-gray-500">{t('learning.aiqQuiz.cannotLoadData')}</p>
                        <button
                            onClick={() => loadQuizScript(currentQuizId)}
                            className="flex gap-2 items-center mt-4 text-indigo-600 hover:text-indigo-800"
                        >
                            <RefreshCw className="w-4 h-4" />
                            {t('learning.aiqQuiz.retry')}
                        </button>
                    </div>
                )}
            </div>

            {/* Footer Navigation */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                <div className="flex justify-between items-center mx-auto max-w-3xl">
                    <button
                        onClick={handlePrevious}
                        disabled={currentIndex === 0}
                        className={cn(
                            'flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors',
                            currentIndex === 0
                                ? 'text-gray-400 cursor-not-allowed'
                                : 'text-gray-700 hover:bg-gray-100'
                        )}
                    >
                        <ChevronLeft className="w-5 h-5" />
                        {t('learning.aiqQuiz.previous')}
                    </button>

                    <span className="text-sm text-gray-500">
                        {currentIndex + 1} / {quizItems.length}
                    </span>

                    {currentIndex < quizItems.length - 1 ? (
                        <button
                            onClick={handleNext}
                            className="flex gap-2 items-center px-4 py-2 font-medium rounded-lg transition-colors text-indigo-600 hover:bg-indigo-50"
                        >
                            {t('learning.aiqQuiz.next')}
                            <ChevronRight className="w-5 h-5" />
                        </button>
                    ) : (
                        <button
                            onClick={handleCompleteClick}
                            disabled={isCompleting}
                            className={cn(
                                'flex gap-2 items-center px-6 py-2 font-medium rounded-lg transition-colors',
                                isCompleting
                                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                    : 'bg-indigo-600 text-white hover:bg-indigo-700'
                            )}
                        >
                            {isCompleting ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    {t('learning.aiqQuiz.completing')}
                                </>
                            ) : (
                                <>
                                    {t('learning.aiqQuiz.finalSubmit')}
                                    <CheckCircle className="w-5 h-5" />
                                </>
                            )}
                        </button>
                    )}
                </div>
            </div>

            {/* 미응답 문제 확인 다이얼로그 */}
            <Dialog open={showSubmitConfirm} onOpenChange={setShowSubmitConfirm}>
                <DialogContent className="sm:max-w-sm">
                    <DialogHeader>
                        <DialogTitle>{t('learning.aiqQuiz.unansweredConfirmTitle')}</DialogTitle>
                    </DialogHeader>
                    <p className="text-gray-600">
                        {t('learning.aiqQuiz.unansweredMessage', { count: unansweredCount })}
                        <br />
                        {t('learning.aiqQuiz.unansweredConfirm')}
                    </p>
                    <DialogFooter>
                        <button
                            onClick={() => setShowSubmitConfirm(false)}
                            className="flex-1 px-4 py-2.5 font-medium text-gray-700 rounded-lg border border-gray-300 transition-colors hover:bg-gray-50"
                        >
                            {t('learning.aiqQuiz.goBack')}
                        </button>
                        <button
                            onClick={handleConfirmSubmit}
                            className="flex-1 px-4 py-2.5 font-medium text-white bg-indigo-600 rounded-lg transition-colors hover:bg-indigo-700"
                        >
                            {t('learning.aiqQuiz.submitAnyway')}
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
}

export default AiqQuizStage
